import {
  ArrowRightOutlined,
  BranchesOutlined,
  DollarOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  RobotOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  AdminPage,
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from '@wanjianshi/admin-ui';
import type { ApiResponse, DashboardSummary } from '@wanjianshi/shared-contracts';
import { ApiError } from '@wanjianshi/shared-http';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/auth/store';
import { adminApi, isAdminUnauthorizedError } from '@/services/api/client';

const numberFormatter = new Intl.NumberFormat('zh-CN');
const decimalFormatter = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 });

function numberValue(value: number | undefined): string {
  return value === undefined ? '-' : numberFormatter.format(value);
}

function decimalValue(value: string | number | undefined): string {
  if (value === undefined || value === null || value === '') return '-';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? decimalFormatter.format(parsed) : '-';
}

function shortDate(value: string): string {
  const [, month, day] = value.split('-');
  return month && day ? `${month}/${day}` : value;
}

function timeLabel(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

function MetricCard({
  icon,
  title,
  value,
  extra,
  tone,
}: {
  icon: ReactNode;
  title: string;
  value: ReactNode;
  extra: ReactNode;
  tone: 'blue' | 'cyan' | 'violet' | 'amber';
}) {
  return (
    <Card className={`dashboard-overview-card dashboard-overview-card--${tone}`}>
      <div className="dashboard-overview-card__top">
        <span className="dashboard-overview-card__icon">{icon}</span>
        <Typography.Text className="dashboard-overview-card__title">{title}</Typography.Text>
      </div>
      <div className="dashboard-overview-card__value">{value}</div>
      <div className="dashboard-overview-card__extra">{extra}</div>
    </Card>
  );
}

function TrendBars({
  title,
  data,
  valueKey,
  color,
  formatter,
}: {
  title: string;
  data: DashboardSummary['finance_trend'];
  valueKey: 'recharge_amount_yuan' | 'net_consumed_points';
  color: string;
  formatter: (value: string) => string;
}) {
  const values = data.map(item => Number(item[valueKey]) || 0);
  const max = Math.max(...values, 0);
  const total = values.reduce((sum, value) => sum + value, 0);

  return (
    <div className="dashboard-trend-block">
      <div className="dashboard-trend-block__header">
        <Typography.Text strong>{title}</Typography.Text>
        <Typography.Text className="dashboard-trend-block__total">合计 {formatter(String(total))}</Typography.Text>
      </div>
      <div className="dashboard-trend-bars" aria-label={title}>
        {data.map((item, index) => {
          const value = values[index] ?? 0;
          const height = max > 0 ? Math.max(5, Math.round((value / max) * 82)) : 3;
          return (
            <div className="dashboard-trend-bars__item" key={item.date} title={`${item.date}：${formatter(String(value))}`}>
              <div className="dashboard-trend-bars__track">
                <span style={{ height, background: color }} />
              </div>
              <Typography.Text className="dashboard-trend-bars__date">{shortDate(item.date)}</Typography.Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get<ApiResponse<DashboardSummary>>('/admin/dashboard/summary');
      setSummary(response.data);
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === 'REQUEST_ABORTED') return;
      if (isAdminUnauthorizedError(reason)) return;
      setError(reason instanceof Error ? reason.message : '工作台数据加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const quickEntries = useMemo(() => [
    { title: '平台用户', description: '查询用户账户与状态', path: '/users', permission: 'wjs-admin:user:query', icon: <UserOutlined /> },
    { title: '团队会员', description: '维护团队套餐与席位', path: '/memberships/team', permission: 'wjs-admin:team-membership:query', icon: <TeamOutlined /> },
    { title: '项目管理', description: '查看个人与团队项目', path: '/projects/personal', permission: 'wjs-admin:project:query', icon: <FolderOpenOutlined /> },
    { title: '任务日志', description: '排查生成任务状态', path: '/generation-tasks', permission: 'wjs-admin:generation-task:query', icon: <RobotOutlined /> },
    { title: '线路编排', description: '管理自动路由线路', path: '/ai/routing', permission: 'wjs-admin:routing:query', icon: <BranchesOutlined /> },
    { title: '财务分析', description: '查看充值与积分趋势', path: '/reports/finance-analysis', permission: 'wjs-admin:report:finance-analysis', icon: <DollarOutlined /> },
  ].filter(item => hasPermission(item.permission)), [hasPermission]);

  const personalProjectRatio = summary?.projects_total
    ? Math.round((summary.personal_projects / summary.projects_total) * 100)
    : 0;
  const teamProjectRatio = summary?.projects_total
    ? Math.round((summary.team_projects / summary.projects_total) * 100)
    : 0;
  const activeTasks = (summary?.tasks_running_today ?? 0) + (summary?.tasks_pending_today ?? 0);

  return (
    <AdminPage
      title="工作台"
      description="平台经营、生成任务与财务数据的统一概览"
      extra={(
        <Space size={10}>
          <Typography.Text type="secondary">更新于 {timeLabel(summary?.generated_at)}</Typography.Text>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>刷新</Button>
        </Space>
      )}
    >
      {error ? <Alert type="error" showIcon message={error} action={<Button type="link" onClick={() => void load()}>重新加载</Button>} /> : null}

      <Spin spinning={loading && !summary}>
        <section className="dashboard-section">
          <div className="dashboard-section__heading">
            <div>
              <Typography.Title level={4}>今日核心指标</Typography.Title>
              <Typography.Text type="secondary">实时统计截至当前时间</Typography.Text>
            </div>
            <Tag color={activeTasks > 0 ? 'processing' : 'success'}>{activeTasks > 0 ? `${activeTasks} 个任务处理中` : '当前无待处理任务'}</Tag>
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} xl={6}>
              <MetricCard
                icon={<UserOutlined />}
                title="平台用户"
                value={numberValue(summary?.users_total)}
                extra={<>今日新增 <strong>+{numberValue(summary?.users_today)}</strong></>}
                tone="blue"
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <MetricCard
                icon={<FolderOpenOutlined />}
                title="项目总量"
                value={numberValue(summary?.projects_total)}
                extra={<>今日新增 <strong>+{numberValue(summary?.projects_today)}</strong></>}
                tone="cyan"
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <MetricCard
                icon={<RobotOutlined />}
                title="今日生成任务"
                value={numberValue(summary?.tasks_today)}
                extra={<>成功率 <strong>{decimalValue(summary?.task_success_rate)}%</strong></>}
                tone="violet"
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <MetricCard
                icon={<DollarOutlined />}
                title="今日充值"
                value={<>¥ {decimalValue(summary?.recharge_today)}</>}
                extra={<><strong>{numberValue(summary?.recharge_orders_today)}</strong> 笔成功订单</>}
                tone="amber"
              />
            </Col>
          </Row>
        </section>

        <Row gutter={[16, 16]} className="dashboard-main-row">
          <Col xs={24} xl={15}>
            <Card
              className="dashboard-panel-card"
              title="近 7 日经营趋势"
              extra={<Typography.Text type="secondary">数据截至 {summary?.report_data_through ?? '暂无汇总'}</Typography.Text>}
            >
              {summary?.finance_trend?.length ? (
                <div className="dashboard-trend-grid">
                  <TrendBars
                    title="充值金额"
                    data={summary.finance_trend}
                    valueKey="recharge_amount_yuan"
                    color="#2563eb"
                    formatter={value => `¥ ${decimalValue(value)}`}
                  />
                  <TrendBars
                    title="净消耗积分"
                    data={summary.finance_trend}
                    valueKey="net_consumed_points"
                    color="#7c3aed"
                    formatter={decimalValue}
                  />
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="日报汇总后将在这里展示趋势" />
              )}
            </Card>
          </Col>

          <Col xs={24} xl={9}>
            <Card className="dashboard-panel-card" title="今日任务运行情况">
              <div className="dashboard-success-rate">
                <div>
                  <Typography.Text type="secondary">任务成功率</Typography.Text>
                  <strong>{decimalValue(summary?.task_success_rate)}%</strong>
                </div>
                <Progress
                  type="circle"
                  size={86}
                  strokeWidth={9}
                  percent={Math.min(100, Math.max(0, summary?.task_success_rate ?? 0))}
                  format={() => numberValue(summary?.tasks_today)}
                />
              </div>
              <div className="dashboard-status-grid">
                <div><span className="dashboard-status-dot dashboard-status-dot--success" />成功<strong>{numberValue(summary?.tasks_succeeded_today)}</strong></div>
                <div><span className="dashboard-status-dot dashboard-status-dot--danger" />失败<strong>{numberValue(summary?.tasks_failed_today)}</strong></div>
                <div><span className="dashboard-status-dot dashboard-status-dot--processing" />运行中<strong>{numberValue(summary?.tasks_running_today)}</strong></div>
                <div><span className="dashboard-status-dot dashboard-status-dot--waiting" />排队中<strong>{numberValue(summary?.tasks_pending_today)}</strong></div>
              </div>
              {(summary?.tasks_failed_today ?? 0) > 0 && hasPermission('wjs-admin:generation-task:query') ? (
                <button className="dashboard-warning-link" type="button" onClick={() => navigate('/generation-tasks?status=failed')}>
                  <WarningOutlined /> 今日有 {summary?.tasks_failed_today} 个失败任务，立即查看
                  <ArrowRightOutlined />
                </button>
              ) : (
                <div className="dashboard-healthy-note">今日任务运行状态正常</div>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={9}>
            <Card className="dashboard-panel-card" title="业务结构">
              <div className="dashboard-structure-total">
                <div><Typography.Text type="secondary">团队总数</Typography.Text><strong>{numberValue(summary?.teams_total)}</strong></div>
                <div><Typography.Text type="secondary">今日积分消耗</Typography.Text><strong>{decimalValue(summary?.credits_used_today)}</strong></div>
              </div>
              <div className="dashboard-structure-item">
                <div><span>个人项目</span><strong>{numberValue(summary?.personal_projects)}</strong></div>
                <Progress percent={personalProjectRatio} showInfo={false} strokeColor="#2563eb" />
              </div>
              <div className="dashboard-structure-item">
                <div><span>团队项目</span><strong>{numberValue(summary?.team_projects)}</strong></div>
                <Progress percent={teamProjectRatio} showInfo={false} strokeColor="#14b8a6" />
              </div>
              <Typography.Text type="secondary" className="dashboard-structure-caption">
                项目构成：个人 {personalProjectRatio}% · 团队 {teamProjectRatio}%
              </Typography.Text>
            </Card>
          </Col>

          <Col xs={24} xl={15}>
            <Card className="dashboard-panel-card" title="快捷入口" extra={<Typography.Text type="secondary">按当前账号权限展示</Typography.Text>}>
              <div className="dashboard-quick-grid">
                {quickEntries.map(item => (
                  <button className="dashboard-quick-entry" type="button" key={item.path} onClick={() => navigate(item.path)}>
                    <span className="dashboard-quick-entry__icon">{item.icon}</span>
                    <span className="dashboard-quick-entry__content">
                      <strong>{item.title}</strong>
                      <small>{item.description}</small>
                    </span>
                    <ArrowRightOutlined className="dashboard-quick-entry__arrow" />
                  </button>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>
    </AdminPage>
  );
}
